/**
 * LogsPage — Audit Log + Run History (P1.6).
 *
 * Две вкладки:
 *  - Audit Log    — GET /audit-logs (действия пользователя в системе)
 *  - Run History  — GET /run-history (запуски пайплайнов)
 *
 * Каждая вкладка: фильтр-бар (поиск + select'ы + опц. дата) → <DataTable> с
 * client-side пагинацией над загруженной страницей. Серверный `total` показан
 * меткой возле таблицы. Export — xlsx через exportSheetsToExcel (без CSV).
 * Клик по строке открывает <Modal> с полным объектом (details / snapshots —
 * pretty-printed JSON); для run-history — кнопка «Download output».
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ColumnDef } from '@tanstack/react-table';
import { ScrollText, Download, Search, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { exportSheetsToExcel } from '@/lib/exportExcel';
import { DataTable } from '@/components/shared/DataTable';
import { Modal } from '@/components/ui/Modal';
import { useAuditLogs, type AuditLogItem } from '@/hooks/useAuditLogs';
import {
  useRunHistory,
  downloadRunOutput,
  type RunHistoryItem,
} from '@/hooks/useRunHistory';
import { useProjects } from '@/hooks/useProjects';

type TabKey = 'audit' | 'runs';

const AUDIT_ACTIONS = [
  'login',
  'logout',
  'password_change',
  'api_key_add',
  'api_key_remove',
  'api_key_exhausted',
  'prompt_change',
  'tariff_change',
  'credits_adjusted',
  'pipeline_start',
  'pipeline_cancel',
  'file_upload',
  'file_delete',
  'export',
  'impersonate',
  'superadmin_login',
  'master_template_upload',
  'master_template_delete',
  'master_template_parse',
  'library_create',
  'library_update',
  'library_delete',
  'library_apply',
  'project_create',
  'project_update',
  'project_delete',
] as const;

const PIPELINE_TYPES = ['search', 'generation', 'matching'] as const;

const RUN_STATUSES = [
  'queued',
  'running',
  'paused',
  'completed',
  'failed',
  'cancelled',
] as const;

const STATUS_TONE: Record<string, string> = {
  completed: 'bg-success-tint/15 text-success border-success-tint/30',
  failed: 'bg-danger-tint/15 text-danger border-danger-tint/30',
  running: 'bg-info-tint/15 text-info border-info-tint/30',
  queued: 'bg-warning-tint/15 text-warning border-warning-tint/30',
  paused: 'bg-warning-tint/15 text-warning border-warning-tint/30',
  cancelled: 'bg-surface-700/40 text-surface-400 border-surface-600',
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/** Преобразует value input type=date (yyyy-mm-dd) в ISO datetime (начала дня). */
function dateToIsoFrom(v: string | undefined): string | null {
  if (!v) return null;
  const d = new Date(`${v}T00:00:00`);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

/** value input type=date → ISO datetime (конец дня). */
function dateToIsoTo(v: string | undefined): string | null {
  if (!v) return null;
  const d = new Date(`${v}T23:59:59`);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

export function LogsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<TabKey>('audit');
  const { data: projectsData } = useProjects();
  const projects = projectsData?.items ?? [];

  // === Общее состояние фильтров (debounce поиска 300ms) ===
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const h = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(h);
  }, [searchInput]);

  const [action, setAction] = useState<string>('');
  const [pipelineType, setPipelineType] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [projectId, setProjectId] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  const fromIso = useMemo(() => dateToIsoFrom(fromDate), [fromDate]);
  const toIso = useMemo(() => dateToIsoTo(toDate), [toDate]);

  // === Audit Log query ===
  const auditFilters = useMemo(
    () => ({
      search: search || null,
      action: action || null,
      project_id: projectId || null,
      from: fromIso,
      to: toIso,
      page: 1,
      page_size: 200,
    }),
    [search, action, projectId, fromIso, toIso],
  );
  const {
    data: auditData,
    isLoading: auditLoading,
    isFetching: auditFetching,
  } = useAuditLogs(auditFilters);

  // === Run History query ===
  const runFilters = useMemo(
    () => ({
      search: search || null,
      pipeline_type: pipelineType || null,
      status: status || null,
      project_id: projectId || null,
      from: fromIso,
      to: toIso,
      page: 1,
      page_size: 200,
    }),
    [search, pipelineType, status, projectId, fromIso, toIso],
  );
  const {
    data: runData,
    isLoading: runLoading,
    isFetching: runFetching,
  } = useRunHistory(runFilters);

  const auditItems = auditData?.items ?? [];
  const runItems = runData?.items ?? [];

  // === Detail modal ===
  const [auditDetail, setAuditDetail] = useState<AuditLogItem | null>(null);
  const [runDetail, setRunDetail] = useState<RunHistoryItem | null>(null);

  function resetFilters() {
    setSearchInput('');
    setAction('');
    setPipelineType('');
    setStatus('');
    setProjectId('');
    setFromDate('');
    setToDate('');
  }

  const hasFilters =
    !!search ||
    !!action ||
    !!pipelineType ||
    !!status ||
    !!projectId ||
    !!fromDate ||
    !!toDate;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold mb-1">
          {t('logs.title', { defaultValue: 'Logs' })}
        </h1>
        <p className="text-sm text-surface-400">
          {t('logs.subtitle', {
            defaultValue: 'Audit log and pipeline run history.',
          })}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-surface-800 overflow-x-auto">
        <TabButton
          active={tab === 'audit'}
          onClick={() => setTab('audit')}
          label={t('logs.tabAudit', { defaultValue: 'Audit Log' })}
        />
        <TabButton
          active={tab === 'runs'}
          onClick={() => setTab('runs')}
          label={t('logs.tabRuns', { defaultValue: 'Run History' })}
        />
      </div>

      {/* Filters bar */}
      <div className="card p-3 flex flex-wrap items-end gap-3">
        <div className="relative flex-1 min-w-[14rem]">
          <Search className="w-4 h-4 text-surface-500 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            className="input pl-8 py-1.5 text-sm w-full"
            placeholder={t('logs.search', { defaultValue: 'Search…' })}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        {tab === 'audit' && (
          <FilterSelect
            label={t('logs.action', { defaultValue: 'Action' })}
            value={action}
            onChange={setAction}
            options={AUDIT_ACTIONS}
          />
        )}

        {tab === 'runs' && (
          <>
            <FilterSelect
              label={t('logs.pipelineType', { defaultValue: 'Type' })}
              value={pipelineType}
              onChange={setPipelineType}
              options={PIPELINE_TYPES}
            />
            <FilterSelect
              label={t('logs.status', { defaultValue: 'Status' })}
              value={status}
              onChange={setStatus}
              options={RUN_STATUSES}
            />
          </>
        )}

        {/* Project filter */}
        <div className="flex flex-col gap-1">
          <label className="text-xs text-surface-500 uppercase tracking-wider">
            {t('logs.project', { defaultValue: 'Project' })}
          </label>
          <select
            className="input py-1.5 text-sm"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          >
            <option value="">
              {t('logs.allProjects', { defaultValue: 'All projects' })}
            </option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        {/* Date range */}
        <div className="flex flex-col gap-1">
          <label className="text-xs text-surface-500 uppercase tracking-wider">
            {t('logs.from', { defaultValue: 'From' })}
          </label>
          <input
            type="date"
            className="input py-1.5 text-sm"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-surface-500 uppercase tracking-wider">
            {t('logs.to', { defaultValue: 'To' })}
          </label>
          <input
            type="date"
            className="input py-1.5 text-sm"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>

        {hasFilters && (
          <button
            type="button"
            onClick={resetFilters}
            className="btn-ghost flex items-center gap-1 text-sm"
          >
            <X className="w-3.5 h-3.5" />
            {t('dataTable.clear', { defaultValue: 'Clear' })}
          </button>
        )}
      </div>

      {/* Table + export */}
      {tab === 'audit' ? (
        <AuditLogTable
          items={auditItems}
          total={auditData?.total ?? 0}
          loading={auditLoading}
          fetching={auditFetching}
          onExport={() => exportAuditLogs(auditItems, t)}
          onRowClick={setAuditDetail}
        />
      ) : (
        <RunHistoryTable
          items={runItems}
          total={runData?.total ?? 0}
          loading={runLoading}
          fetching={runFetching}
          onExport={() => exportRunHistory(runItems, t)}
          onRowClick={setRunDetail}
        />
      )}

      {/* Detail modals */}
      <AuditDetailModal item={auditDetail} onClose={() => setAuditDetail(null)} />
      <RunDetailModal item={runDetail} onClose={() => setRunDetail(null)} t={t} />
    </div>
  );
}

// === Tab button ===

function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
        active
          ? 'border-primary-500 text-primary-300'
          : 'border-transparent text-surface-400 hover:text-surface-200',
      )}
    >
      <ScrollText className="w-4 h-4" />
      {label}
    </button>
  );
}

// === Filter select ===

function FilterSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-surface-500 uppercase tracking-wider">
        {label}
      </label>
      <select
        className="input py-1.5 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">
          {t('logs.all', { defaultValue: 'All' })}
        </option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

// === Audit Log table ===

function AuditLogTable({
  items,
  total,
  loading,
  fetching,
  onExport,
  onRowClick,
}: {
  items: AuditLogItem[];
  total: number;
  loading: boolean;
  fetching: boolean;
  onExport: () => void;
  onRowClick: (item: AuditLogItem) => void;
}) {
  const { t } = useTranslation();

  const columns = useMemo<ColumnDef<AuditLogItem>[]>(
    () => [
      {
        id: 'created_at',
        accessorKey: 'created_at',
        header: () => t('logs.created', { defaultValue: 'Created' }),
        cell: (info) => (
          <span className="text-surface-300 whitespace-nowrap tabular-nums">
            {fmtDate(info.getValue<string>())}
          </span>
        ),
        size: 180,
      },
      {
        id: 'action',
        accessorKey: 'action',
        header: () => t('logs.action', { defaultValue: 'Action' }),
        cell: (info) => (
          <span className="text-[10px] px-2 py-0.5 rounded border border-surface-600 bg-surface-700/40 text-surface-200 uppercase tracking-wider whitespace-nowrap">
            {info.getValue<string>()}
          </span>
        ),
        size: 170,
      },
      {
        id: 'resource_type',
        accessorKey: 'resource_type',
        header: () => t('logs.resourceType', { defaultValue: 'Resource' }),
        cell: (info) => (
          <span className="text-surface-300">{info.getValue<string>() ?? '—'}</span>
        ),
        size: 140,
      },
      {
        id: 'description',
        accessorKey: 'description',
        header: () => t('logs.description', { defaultValue: 'Description' }),
        cell: (info) => (
          <span className="text-surface-200 line-clamp-2 max-w-[28rem]">
            {info.getValue<string>() ?? '—'}
          </span>
        ),
        size: 320,
      },
      {
        id: 'actor',
        accessorFn: (d) => d.actor_account_id ?? '—',
        header: () => t('logs.actor', { defaultValue: 'Actor' }),
        cell: (info) => (
          <span className="font-mono text-xs text-surface-400">
            {shortId(info.getValue<string>())}
          </span>
        ),
        size: 120,
      },
    ],
    [t],
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-surface-500 tabular-nums">
          {total} {t('logs.records', { defaultValue: 'records' })}
        </span>
        <button
          type="button"
          onClick={onExport}
          disabled={items.length === 0}
          className="btn-secondary flex items-center gap-1.5 text-sm disabled:opacity-40"
        >
          <Download className="w-4 h-4" />
          {t('logs.export', { defaultValue: 'Export' })}
        </button>
      </div>
      <DataTable
        columns={columns}
        data={items}
        onRowClick={onRowClick}
        isLoading={loading || fetching}
        emptyState={t('logs.noData', { defaultValue: 'No records' })}
      />
    </div>
  );
}

// === Run History table ===

function RunHistoryTable({
  items,
  total,
  loading,
  fetching,
  onExport,
  onRowClick,
}: {
  items: RunHistoryItem[];
  total: number;
  loading: boolean;
  fetching: boolean;
  onExport: () => void;
  onRowClick: (item: RunHistoryItem) => void;
}) {
  const { t } = useTranslation();

  const columns = useMemo<ColumnDef<RunHistoryItem>[]>(
    () => [
      {
        id: 'started_at',
        accessorFn: (d) => d.started_at ?? d.created_at,
        header: () => t('logs.started', { defaultValue: 'Started' }),
        cell: (info) => (
          <span className="text-surface-300 whitespace-nowrap tabular-nums">
            {fmtDate(info.getValue<string>())}
          </span>
        ),
        size: 180,
      },
      {
        id: 'pipeline_type',
        accessorKey: 'pipeline_type',
        header: () => t('logs.pipelineType', { defaultValue: 'Type' }),
        cell: (info) => (
          <span className="text-[10px] px-2 py-0.5 rounded border border-primary-500/30 bg-primary-500/10 text-primary-300 uppercase tracking-wider whitespace-nowrap">
            {info.getValue<string>()}
          </span>
        ),
        size: 130,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: () => t('logs.status', { defaultValue: 'Status' }),
        cell: (info) => {
          const s = info.getValue<string>();
          return (
            <span
              className={cn(
                'text-[10px] px-2 py-0.5 rounded border uppercase tracking-wider whitespace-nowrap',
                STATUS_TONE[s] ?? 'bg-surface-700/40 text-surface-300 border-surface-600',
              )}
            >
              {s}
            </span>
          );
        },
        size: 130,
      },
      {
        id: 'rows',
        accessorFn: (d) => (d.total_rows > 0 ? `${d.success_rows}/${d.total_rows}` : '—'),
        header: () => t('logs.rows', { defaultValue: 'Rows' }),
        cell: (info) => (
          <span className="tabular-nums text-surface-200 whitespace-nowrap">
            {info.getValue<string>()}
          </span>
        ),
        size: 110,
      },
      {
        id: 'spent_credits',
        accessorKey: 'spent_credits',
        header: () => t('logs.credits', { defaultValue: 'Credits' }),
        cell: (info) => {
          const v = info.getValue<string | null>();
          return (
            <span className="tabular-nums text-surface-200 whitespace-nowrap">
              {v != null && v !== '' ? v : '—'}
            </span>
          );
        },
        size: 120,
      },
      {
        id: 'is_test_run',
        accessorKey: 'is_test_run',
        header: () => t('logs.testRun', { defaultValue: 'Test' }),
        cell: (info) =>
          info.getValue<boolean>() ? (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-warning-tint/15 text-warning border border-warning-tint/30 uppercase tracking-wider">
              test
            </span>
          ) : (
            <span className="text-surface-500">—</span>
          ),
        size: 80,
      },
    ],
    [t],
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-surface-500 tabular-nums">
          {total} {t('logs.records', { defaultValue: 'records' })}
        </span>
        <button
          type="button"
          onClick={onExport}
          disabled={items.length === 0}
          className="btn-secondary flex items-center gap-1.5 text-sm disabled:opacity-40"
        >
          <Download className="w-4 h-4" />
          {t('logs.export', { defaultValue: 'Export' })}
        </button>
      </div>
      <DataTable
        columns={columns}
        data={items}
        onRowClick={onRowClick}
        isLoading={loading || fetching}
        emptyState={t('logs.noData', { defaultValue: 'No records' })}
      />
    </div>
  );
}

// === Detail modals ===

function AuditDetailModal({
  item,
  onClose,
}: {
  item: AuditLogItem | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      open={item !== null}
      onClose={onClose}
      title={t('logs.details', { defaultValue: 'Details' })}
      maxWidth="2xl"
    >
      {item && (
        <div className="space-y-3 max-h-[75vh] overflow-y-auto">
          <DetailRow
            label={t('logs.created', { defaultValue: 'Created' })}
            value={fmtDate(item.created_at)}
          />
          <DetailRow label={t('logs.action', { defaultValue: 'Action' })} value={item.action} />
          <DetailRow
            label={t('logs.resourceType', { defaultValue: 'Resource' })}
            value={item.resource_type ?? '—'}
          />
          <DetailRow
            label="Resource ID"
            value={item.resource_id ?? '—'}
            mono
          />
          <DetailRow
            label={t('logs.project', { defaultValue: 'Project' })}
            value={item.project_id ?? '—'}
            mono
          />
          <DetailRow
            label={t('logs.actor', { defaultValue: 'Actor' })}
            value={item.actor_account_id ?? '—'}
            mono
          />
          <DetailRow label="IP" value={item.ip_address ?? '—'} mono />
          <DetailRow
            label={t('logs.description', { defaultValue: 'Description' })}
            value={item.description ?? '—'}
          />
          <div>
            <div className="text-xs text-surface-500 uppercase tracking-wider mb-1">
              {t('logs.details', { defaultValue: 'Details' })}
            </div>
            <pre className="bg-surface-800/40 rounded-lg p-3 text-xs overflow-x-auto">
              {item.details ? JSON.stringify(item.details, null, 2) : 'null'}
            </pre>
          </div>
        </div>
      )}
    </Modal>
  );
}

function RunDetailModal({
  item,
  onClose,
  t,
}: {
  item: RunHistoryItem | null;
  onClose: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  async function handleDownload(id: string) {
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadRunOutput(id);
    } catch (e) {
      setDownloadError(
        e instanceof Error ? e.message : 'Download failed',
      );
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Modal
      open={item !== null}
      onClose={onClose}
      title={t('logs.details', { defaultValue: 'Details' })}
      maxWidth="2xl"
      footer={
        item && (
          <button
            type="button"
            onClick={() => handleDownload(item.id)}
            disabled={downloading}
            className="btn-primary flex items-center gap-1.5 text-sm disabled:opacity-40"
          >
            <Download className="w-4 h-4" />
            {downloading
              ? t('common.loading', { defaultValue: 'Loading...' })
              : t('logs.downloadOutput', { defaultValue: 'Download output' })}
          </button>
        )
      }
    >
      {item && (
        <div className="space-y-3 max-h-[75vh] overflow-y-auto">
          <DetailRow
            label={t('logs.started', { defaultValue: 'Started' })}
            value={fmtDate(item.started_at ?? item.created_at)}
          />
          <DetailRow
            label={t('logs.completed', { defaultValue: 'Completed' })}
            value={fmtDate(item.completed_at)}
          />
          <DetailRow
            label={t('logs.pipelineType', { defaultValue: 'Type' })}
            value={item.pipeline_type}
          />
          <DetailRow
            label={t('logs.status', { defaultValue: 'Status' })}
            value={item.status}
          />
          <DetailRow
            label={t('logs.rows', { defaultValue: 'Rows' })}
            value={`${item.success_rows}/${item.total_rows} (errors: ${item.error_rows})`}
          />
          <DetailRow
            label={t('logs.credits', { defaultValue: 'Credits' })}
            value={`spent: ${item.spent_credits ?? '—'} / est: ${item.estimated_credits ?? '—'}`}
          />
          {item.error_message && (
            <div>
              <div className="text-xs text-surface-500 uppercase tracking-wider mb-1">
                {t('logs.error', { defaultValue: 'Error' })}
              </div>
              <pre className="bg-danger-tint/10 rounded-lg p-3 text-xs overflow-x-auto text-danger border border-danger-tint/20">
                {item.error_message}
              </pre>
            </div>
          )}
          <SnapshotBlock
            label={t('logs.inputSnapshot', { defaultValue: 'Input snapshot' })}
            data={item.input_snapshot}
          />
          <SnapshotBlock
            label={t('logs.outputSnapshot', { defaultValue: 'Output snapshot' })}
            data={item.output_snapshot}
          />
          {downloadError && (
            <div className="text-xs text-danger">{downloadError}</div>
          )}
        </div>
      )}
    </Modal>
  );
}

function SnapshotBlock({
  label,
  data,
}: {
  label: string;
  data: Record<string, unknown> | null;
}) {
  return (
    <div>
      <div className="text-xs text-surface-500 uppercase tracking-wider mb-1">
        {label}
      </div>
      <pre className="bg-surface-800/40 rounded-lg p-3 text-xs overflow-x-auto">
        {data ? JSON.stringify(data, null, 2) : 'null'}
      </pre>
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-xs text-surface-500 uppercase tracking-wider w-32 shrink-0 pt-0.5">
        {label}
      </span>
      <span className={cn('text-sm text-surface-200 break-all', mono && 'font-mono text-xs')}>
        {value}
      </span>
    </div>
  );
}

// === Export helpers ===

function exportAuditLogs(items: AuditLogItem[], t: ReturnType<typeof useTranslation>['t']) {
  if (items.length === 0) return;
  const rows = items.map((it) => ({
    [t('logs.created', { defaultValue: 'Created' })]: fmtDate(it.created_at),
    [t('logs.action', { defaultValue: 'Action' })]: it.action,
    [t('logs.resourceType', { defaultValue: 'Resource' })]: it.resource_type ?? '',
    'Resource ID': it.resource_id ?? '',
    [t('logs.project', { defaultValue: 'Project' })]: it.project_id ?? '',
    [t('logs.actor', { defaultValue: 'Actor' })]: it.actor_account_id ?? '',
    'IP': it.ip_address ?? '',
    [t('logs.description', { defaultValue: 'Description' })]: it.description ?? '',
    'Details': it.details ? JSON.stringify(it.details) : '',
  }));
  exportSheetsToExcel('audit-logs', [{ name: 'Audit Log', rows }]);
}

function exportRunHistory(
  items: RunHistoryItem[],
  t: ReturnType<typeof useTranslation>['t'],
) {
  if (items.length === 0) return;
  const rows = items.map((it) => ({
    [t('logs.started', { defaultValue: 'Started' })]: fmtDate(it.started_at ?? it.created_at),
    [t('logs.completed', { defaultValue: 'Completed' })]: fmtDate(it.completed_at),
    [t('logs.pipelineType', { defaultValue: 'Type' })]: it.pipeline_type,
    [t('logs.status', { defaultValue: 'Status' })]: it.status,
    [t('logs.rows', { defaultValue: 'Rows' })]: `${it.success_rows}/${it.total_rows}`,
    'Errors': it.error_rows,
    [t('logs.credits', { defaultValue: 'Credits' })]: it.spent_credits ?? '',
    'Estimated': it.estimated_credits ?? '',
    [t('logs.testRun', { defaultValue: 'Test' })]: it.is_test_run ? 'yes' : 'no',
    [t('logs.error', { defaultValue: 'Error' })]: it.error_message ?? '',
  }));
  exportSheetsToExcel('run-history', [{ name: 'Run History', rows }]);
}

// === utils ===

function shortId(id: string): string {
  if (id === '—' || !id) return '—';
  return id.length > 8 ? id.slice(0, 8) : id;
}